/**
 * Affiliate program API slice (partner-facing dashboard + click mirror).
 * Leaf module (Perf 3.1.b/e pattern): pulls only the shared chokepoint from
 * ./client; never imports a sibling @/lib/api slice.
 *
 * Backend contract (affiliate plan §4/§6): /json/affiliate/* answers 200 with
 * `{ success: true, data: {...} }`; every failure is a non-2xx status with
 * `{ success: false, error: <code> }`, which `fetchWithAuth` already throws as
 * an ApiError before these guards see it. Money amounts are integer minor
 * units and are never summed across currencies — the UI keeps them separate.
 */
import { fetchWithAuth, fetchWithTimeout, API_HOST, ApiError } from './client';

function unwrap(data, fallbackMessage) {
  if (!data || data.success !== true) {
    throw new ApiError((data && (data.error || data.message)) || fallbackMessage, 400, data);
  }
  return data.data;
}

export const affiliateApi = {
  captureNativeClick: async (slug) => {
    // Public entry: do not wait for credentials or send a departing account's
    // token. Authentication is required only when claiming the signed receipt.
    const response = await fetchWithTimeout(`${API_HOST}/json/affiliate/native-click`, {
      method: 'POST', credentials: 'omit', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ slug }),
    }, 5000);
    if (!response.ok) throw new ApiError('Unable to record referral', response.status);
    return unwrap(await response.json(), 'Unable to record referral');
  },
  claimNativeClick: async (receipt, isRequestCurrent) => unwrap(await fetchWithAuth('/json/affiliate/native-claim', {
    method: 'POST', body: { receipt }, timeoutMs: 5000, networkRetries: 0, isRequestCurrent,
  }), 'Unable to retain referral'),
  // One-screen partner overview: per-currency balances, in-flight payout
  // batches, active links (with codes) and the recent anonymized ledger.
  // Unenrolled visitors receive 404. Enrolled former partners retain history;
  // enrollment.canParticipate controls new activity and acceptance.
  getSummary: async () => {
    const data = await fetchWithAuth('/json/affiliate/summary');
    return unwrap(data, 'Failed to load the affiliate overview');
  },

  requestPayout: async () => unwrap(await fetchWithAuth('/json/affiliate/payout-request', { method: 'POST', networkRetries: 0, body: {} }), 'Unable to confirm payout request'),

  getBankDetails: async () => unwrap(await fetchWithAuth('/json/affiliate/bank-details'), 'Failed to load payment status'),
  requestVerification: async (purpose) => unwrap(await fetchWithAuth('/json/affiliate/verification', { method: 'POST', networkRetries: 0, body: { purpose } }), 'Unable to send verification code'),
  submitSecureForm: async (purpose, body) => unwrap(await fetchWithAuth(purpose === 'w9' ? '/json/affiliate/documents/w9' : '/json/affiliate/bank-details', { method: 'POST', networkRetries: 0, body }), 'Unable to confirm submission'),

  getDocuments: async () => unwrap(await fetchWithAuth('/json/affiliate/documents'), 'Failed to load tax status'),

  uploadDocument: async (asset) => {
    const data = await fetchWithAuth('/json/affiliate/documents', {
      method: 'POST', networkRetries: 0,
      prepareRequest: () => {
        const body = new FormData();
        body.append('document', asset.file || { uri: asset.uri, name: asset.name || 'w9.pdf', type: asset.mimeType || 'application/pdf' });
        return { body };
      },
    });
    return unwrap(data, 'Could not upload the tax document');
  },

  getEarnings: async ({ currency, from, to, limit = 50 } = {}) => {
    const params = [];
    if (typeof currency === 'string' && /^[A-Z]{3}$/.test(currency)) params.push('currency=' + currency);
    if (from) params.push('from=' + encodeURIComponent(from));
    if (to) params.push('to=' + encodeURIComponent(to));
    params.push('limit=' + Math.min(200, Math.max(1, Number(limit) || 50)));
    const data = await fetchWithAuth('/json/affiliate/earnings?' + params.join('&'));
    return unwrap(data, 'Failed to load the earnings history').entries || [];
  },

  getPayouts: async () => {
    const data = await fetchWithAuth('/json/affiliate/payouts');
    return unwrap(data, 'Failed to load the payout batches').payouts || [];
  },

  getPayoutStatement: async (batchId) => {
    const data = await fetchWithAuth('/json/affiliate/payouts/' + encodeURIComponent(batchId));
    return unwrap(data, 'Failed to load the payout statement').statements || [];
  },

  // The terms version pending acceptance (or the accepted one) for this
  // partner only; null means there is no agreement on file yet.
  getAgreement: async () => {
    const data = await fetchWithAuth('/json/affiliate/agreement');
    return unwrap(data, 'Failed to load the agreement').terms || null;
  },

  acceptAgreement: async (termsId, agreementHash, agreementVersion) => {
    const data = await fetchWithAuth('/json/affiliate/agreement/accept', {
      method: 'POST',
      body: { termsId, agreementHash, agreementVersion },
    });
    return unwrap(data, 'Could not record your acceptance');
  },

  // In-app affiliate-link click mirror (affiliate plan §2/§3): records the
  // click for a signed-in user without overwriting a saved attribution, and
  // applies the same 10-per-5-minutes and terms-acceptance gates as /r/:slug.
  // Short timeout + no retries: a cold-start deep-link must not stall the
  // first navigation behind a 30-second chokepoint default.
  recordClick: async (slug) => {
    const data = await fetchWithAuth('/json/affiliate/click', {
      method: 'POST',
      body: { slug },
      timeoutMs: 5000,
      networkRetries: 0,
    });
    return !!(data && data.success === true && data.data && data.data.recorded === true);
  },
};
